<template>
  <v-container id="order-details" fluid v-if="order">
    <v-card outlined>
      <v-container fluid>
        <order-headers-mobile :order="order" v-if="isMobile" />
        <order-headers :order="order" v-else />

        <v-toolbar v-if="!isMobile" flat>
          <v-spacer></v-spacer>
          <v-btn outlined fab small right @click="listDisplay = !listDisplay">
            <v-icon v-if="listDisplay">mdi-apps</v-icon>
            <v-icon v-else>mdi-format-list-bulleted-square</v-icon>
          </v-btn>
        </v-toolbar>

        <span v-for="(subOrder, index) in order.sub_orders" :key="subOrder._id">
          <span
            class="d-flex justify-space-between align-center expansion-header pr-3-5"
            @click="toggleExpand(subOrder.order_id)"
          >
            <div v-if="isMobile" class="pr-0 pl-1 py-0 v-list-item__subtitle text-uppercase">
              {{ subOrder.order_id }} - {{ getStatusTranslation(subOrder.current_status) }}

              <span v-if="mustDisplayAnyLocker(subOrder)">
                <span v-if="subOrder.reception_locker_id">
                  <span v-if="subOrder.reception_locker_id !== 'no_available_locker'">
                    {{ $t('subOrder.inLocker') + ' ' + subOrder.reception_locker_id }}
                  </span>
                </span>
                <span v-else-if="mustDisplayPreparationLocker(subOrder)">
                  {{ $t('subOrder.inLocker') + ' ' + subOrder.preparation_locker_id }}
                </span>
              </span>
            </div>
            <div style="display: block" class="py-0 pl-4 v-list-item__title text-uppercase" v-else>
              {{ $t('order.order') }} {{ getTypeTranslation(subOrder.type) }} N°{{ subOrder.order_id }} -
              {{ getStatusTranslation(subOrder.current_status) }}
              <span v-if="mustDisplayAnyLocker(subOrder)">
                <span v-if="subOrder.reception_locker_id">
                  <span v-if="subOrder.reception_locker_id !== 'no_available_locker'">
                    {{ $t('subOrder.inLocker') + ' ' + subOrder.reception_locker_id }}
                  </span>
                </span>
                <span v-else-if="mustDisplayPreparationLocker(subOrder)">
                  {{ $t('subOrder.inLocker') + ' ' + subOrder.preparation_locker_id }}
                </span>
              </span>
            </div>
            <v-btn icon color="primary" class="mr-2">
              <v-icon v-if="expand[subOrder.order_id]">mdi-chevron-up</v-icon>
              <v-icon v-else>mdi-chevron-down</v-icon>
            </v-btn>
          </span>
          <v-divider />
          <span v-if="expand[subOrder.order_id]">
            <!-- list display mobile-->
            <span v-if="isMobile">
              <product-list-mobile :subOrder="subOrder" :customizationsPanel="customizationsPanel[index]" />
            </span>
            <!-- list display -->
            <span v-else-if="listDisplay">
              <product-list :subOrder="subOrder" :customizationsPanel="customizationsPanel[index]" />
            </span>
            <!--card display -->
            <span v-else>
              <product-card-display :subOrder="subOrder" :customizationsPanel="customizationsPanel[index]" />
            </span>
            <v-divider />
          </span>
        </span>
        <span v-if="!order.sub_orders || !order.sub_orders.length">
          <v-divider />
          <span v-if="isMobile">
            <product-list-mobile :subOrder="order" :customizationsPanel="customizationsPanel[0]" />
          </span>
          <span v-else-if="listDisplay">
            <product-list :subOrder="order" :customizationsPanel="customizationsPanel[0]" />
          </span>
          <span v-else>
            <product-card-display :subOrder="order" :customizationsPanel="customizationsPanel[0]" />
          </span>
          <v-divider v-if="shouldDisplayActions" />
        </span>
        <v-card-text class="my-2" v-if="shouldDisplayActions">
          <v-row v-if="shouldDisplayParcelDeliveryButton">
            <v-col>
              <v-row v-if="this.checkPickupCode" justify="center">
                <v-alert dense type="warning" text class="mb-0">
                  <div>{{ $t('subOrder.pickupCodeMessage') }}</div>
                </v-alert>
              </v-row>
              <v-row v-if="pickupCodeResendAllowed" justify="center">
                <div class="d-flex justify-space-around pt-1 pb-4">
                  <v-btn
                    text
                    small
                    color="primary"
                    :ripple="false"
                    class="body-2 text-decoration-underline pa-0"
                    :loading="loadingResendPickupCode"
                    @click="resendProductsDialog = true"
                  >
                    {{ $t('order.resendPickupCodeDialog') }}
                  </v-btn>
                </div>
              </v-row>
              <v-row justify="center" class="pt-1">
                <v-btn
                  class="v-btn-footer"
                  :large="!isMobile"
                  :medium="isMobile"
                  color="primary"
                  depressed
                  tile
                  :disabled="loadingBtn"
                  :loading="loadingBtn"
                  @click="openCustomerDeliveryModalOrUpdate('delivered')"
                >
                  {{ $t('subOrder.PackageDeliveredToCustomer') }}
                </v-btn>
              </v-row>
            </v-col>
          </v-row>
          <v-row v-if="canDisplayModifyPickupDeadlineButton">
            <v-col>
              <v-row justify="center">
                <v-btn
                  class="v-btn-footer mt-2"
                  :large="!isMobile"
                  :medium="isMobile"
                  depressed
                  outlined
                  tile
                  :disabled="loadingBtn || maxExtensionDateReached"
                  :loading="loadingBtn"
                  @click="dateSelectionDialog = true"
                >
                  {{ $t('order.modifyPickupDeadline') }}
                </v-btn>
              </v-row>
            </v-col>
          </v-row>
          <v-row v-if="canDisplayCancelOrderButton">
            <v-col>
              <v-row justify="center">
                <v-btn
                  class="v-btn-footer mt-2"
                  :large="!isMobile"
                  :medium="isMobile"
                  :loading="cancellation"
                  :disabled="cancellation"
                  color="secondary"
                  outlined
                  tile
                  @click="cancelOrderDialog = true"
                >
                  {{ $t('button.cancelOrder') }}
                </v-btn>
              </v-row>
            </v-col>
          </v-row>
          <v-row v-if="canDisplayReturnCreationButton">
            <v-col class="text-center">
              <v-card-text v-if="orderAlreadyFullyReturned(order)">
                {{ $t('order.allProductsReturned') }}
              </v-card-text>
              <v-btn class="v-btn-footer" v-else tile depressed color="primary" @click="createReturn(order.order_id)">
                {{ $t('order.createReturn') }}
              </v-btn>
            </v-col>
          </v-row>
          <v-row v-if="canValidatePayments" class="mb-2" justify="center">
            <v-btn
              :disabled="loadingPaymentValidation"
              :large="!isMobile"
              :loading="loadingPaymentValidation"
              :medium="isMobile"
              class="v-btn-footer"
              color="primary"
              depressed
              tile
              @click="paymentValidationDialog = true"
            >
              {{ $t('order.validatePaymentsBtn') }}
            </v-btn>
          </v-row>
        </v-card-text>
        <generic-expandable-list
          v-if="mustDisplayDocuments"
          :items="sortedDocuments"
          url-field="item.url"
          reference-field="item.name"
          :panel-text-hide="$t('order.documents.hideDocuments')"
          :panel-text-show="$t('order.documents.displayDocuments')"
          :display-upper-divider="false"
        ></generic-expandable-list>
        <customer-details-bar
          v-if="mustDisplayCustomerInformation"
          :customer="order.customer"
          :shippingAddress="order.shipping_address"
          :billingAddress="order.billing_address"
        />
      </v-container>
    </v-card>
    <!-- modals -->
    <customer-delivery-dialog
      v-if="customerDeliveryDialog"
      v-model="customerDeliveryDialog"
      @validateCustomerDelivery="validateCustomerDelivery($event)"
      :has-pickup-code="order.has_pickup_code"
      :item="targetItem"
      :order-type="order.type"
    />
    <cancel-order-dialog
      v-if="cancelOrderDialog"
      v-model="cancelOrderDialog"
      :choices="cancelReasons"
      :title="$t('button.cancelOrder')"
      :introductionSentence="$t('subOrder.cancelReasonText')"
      @confirm="cancel"
    />
    <date-selection-dialog
      v-if="dateSelectionDialog"
      v-model="dateSelectionDialog"
      :maxDate="maxExtensionDate"
      :title="$t('order.modifyPickupDeadline')"
      :introductionSentence="$t('order.modifyPickupDeadlineMessage')"
      :currentDeadline="pickupDeadline"
      :filterDateFunction="deadlinesAreValid"
      @close="dateSelectionDialog = false"
      @validate="onDeadlineChange"
    ></date-selection-dialog>
    <confirmation-dialog
      v-model="resendProductsDialog"
      :title="$t('order.resendPickupCodeDialog')"
      :text="$t('order.resendPickupCodeDialogMessage')"
      :loading="loadingResendPickupCode"
      @no="resendProductsDialog = false"
      @yes="resendPickupCode"
    ></confirmation-dialog>
    <payment-validation-dialog
      v-if="paymentValidationDialog"
      v-model="paymentValidationDialog"
      :order="order"
      :payment-methods="allowedPaymentMethods"
      :loading="loadingPaymentValidation"
      @validateForm="validatePayments"
      @close="paymentValidationDialog = false"
    />
  </v-container>
</template>

<script>
  import CustomerDeliveryDialog from '@/components/SubOrders/CustomerDeliveryDialog.vue'
  import CancelOrderDialog from '@/components/MultipleChoicesDialog.vue'
  import CustomerDetailsBar from '@/components/CustomerDetailsBar.vue'
  import ProductListMobile from '@/components/products/ProductListMobile.vue'
  import ProductList from '@/components/products/ProductList.vue'
  import ProductCardDisplay from '@/components/products/ProductCardDisplay.vue'
  import OrderHeadersMobile from '@/components/orders/OrderHeadersMobile.vue'
  import OrderHeaders from '@/components/orders/OrderHeaders.vue'
  import { mapState } from 'vuex'
  import { EventBus } from '@/event-bus'
  import dateSelectionDialog from '@/components/dialogs/DateSelectionDialog.vue'
  import confirmationDialog from '@/components/ConfirmationDialog.vue'
  import {
    checkDeadlineIsValid,
    computeMaxExtensionDate,
    canDisplayPickupDeadlineButton,
    applyPickupLocationToComponent,
    updatePickupDeadline,
    maxExtensionDateReached,
  } from '../../helpers/pickupDeadlineHelper'
  import { resolvePickupCancelRoles } from '@/helpers/pickupCancelPermission'
  import { OrderSettingsService } from '@/services/orderSettingsService'
  import { OrderService } from '@/services/orderService'
  import { ReturnSettingsService } from '@/services/returnSettingsService.js'
  import { DocumentService } from '@/services/documentService'
  import GenericExpandableList from '@/components/GenericExpandableList.vue'
  import { getOrderDocTypeTranslation } from '../../helpers/helper'
  import PaymentValidationDialog from '@/components/dialogs/PaymentValidationDialog.vue'
  import { isPaymentValidationStore } from '@/helpers/paymentValidation'

  export default {
    name: 'orderDetailsSeller',
    components: {
      CustomerDetailsBar,
      CustomerDeliveryDialog,
      CancelOrderDialog,
      ProductListMobile,
      ProductList,
      ProductCardDisplay,
      OrderHeadersMobile,
      OrderHeaders,
      dateSelectionDialog,
      confirmationDialog,
      GenericExpandableList,
      PaymentValidationDialog,
    },
    data() {
      return {
        order: null,
        printInvoice: false,
        printDeliveryForm: false,
        printTransporterEtiquette: false,
        printReturnVoucher: false,
        targetItem: undefined,
        defaultImage: require('@/assets/img/products/product_default.png'),
        loadingBtn: false,
        cancellation: false,
        customerDeliveryDialog: false,
        cancelOrderDialog: false,
        listDisplay: true,
        status: [
          { text: this.$t('order.waiting'), value: 'waiting' },
          { text: this.$t('order.waitingPayment'), value: 'waiting_payment' },
          { text: this.$t('order.confirmed'), value: 'confirmed' },
          { text: this.$t('order.partiallyConfirmed'), value: 'partially_confirmed' },
          { text: this.$t('order.toPickup'), value: 'to_pickup' },
          { text: this.$t('order.shippingInProgress'), value: 'shipping_in_progress' },
          { text: this.$t('order.partialShippingInProgress'), value: 'partial_shipping_in_progress' },
          { text: this.$t('order.delivered'), value: 'delivered' },
          { text: this.$t('order.deliveredPartially'), value: 'partially_delivered' },
          { text: this.$t('order.waitingCarrier'), value: 'waiting_carrier' },
          { text: this.$t('order.cancelled'), value: 'cancelled' },
          { text: this.$t('subOrder.waitingAllocation'), value: 'waiting_allocation' },
          { text: this.$t('order.blocked'), value: 'blocked' },
        ],
        orderTypeTranslations: [
          { text: this.$t('subOrder.shipFromWarehouse'), value: 'ship_from_warehouse' },
          { text: this.$t('subOrder.clickAndCollect'), value: 'click_and_collect' },
          { text: this.$t('subOrder.inStorePickup'), value: 'in_store_pickup' },
          { text: this.$t('subOrder.shipFromStore'), value: 'ship_from_store' },
          { text: this.$t('subOrder.clickAndReserve'), value: 'click_and_reserve' },
          { text: this.$t('subOrder.interStore'), value: 'inter_store' },
          { text: this.$t('order.homeDelivery'), value: 'home_delivery' },
          { text: this.$t('order.storeDelivery'), value: 'store_delivery' },
        ],
        expand: {},
        orderSettings: {},
        customizationsPanel: [],
        cancelReasons: [],
        dateSelectionDialog: false,
        pickupLocation: undefined,
        pickupLocationSettings: undefined,
        resendProductsDialog: false,
        loadingResendPickupCode: false,
        returnSettingsService: null,
        allowReturnCreationInShop: [],
        paymentValidationDialog: false,
        loadingPaymentValidation: false,
        documentService: null,
        documents: [],
      }
    },
    created() {
      this.returnSettingsService = new ReturnSettingsService(this.$auth)
      this.documentService = new DocumentService(this.$auth)
    },
    async mounted() {
      this.loadingBtn = true
      if (this.$route.params.id) {
        await this.get()
        await applyPickupLocationToComponent(this)
        this.setCancelReasons(this.pickupLocationSettings)
        if (this.currentStoreDetails?.address?.country_id) {
          this.orderSettings = await OrderSettingsService.get(
            this.$auth.accessToken,
            this.currentStoreDetails.address.country_id
          )
          if (this.orderIsEligibleToReturn(this.order) && this.partnerSettings.returns_access) {
            await this.getReturnSettings(this.currentStoreDetails.address.country_id)
          }
        }
      }
      this.loadingBtn = false
    },
    computed: {
      isMobile() {
        return this.$vuetify.breakpoint.width < 650
      },
      ...mapState([
        'roleRights',
        'partnerSettings',
        'currentStoreDetails',
        'isAdmin',
        'currentStore',
        'roles',
        'isOctipasAdmin',
        'isCustomerService',
      ]),
      canDisplayCancelOrderButton() {
        if (!this.order || this.order.current_status !== 'to_pickup') {
          return false
        }
        // Central roles are not subject to the shop-role lists, like on the API.
        if (this.isCustomerService) {
          return true
        }
        if (!this.pickupLocationSettings) {
          return false
        }
        const allowedRoles = resolvePickupCancelRoles(this.order, this.pickupLocationSettings)
        return Array.isArray(this.roles) && this.roles.some((role) => allowedRoles.includes(role))
      },
      shouldDisplayParcelDeliveryButton: function () {
        const storeDeliveryWithActivatedButton =
          this.orderSettings?.display?.display_parcel_delivery_button !== false &&
          this.order.type !== 'click_and_reserve'
        const clickAndReserveWithActivatedButton =
          this.orderSettings?.display?.display_ebooking_delivery_button !== false &&
          this.order.type === 'click_and_reserve'
        return (
          this.order.current_status === 'to_pickup' &&
          (storeDeliveryWithActivatedButton || clickAndReserveWithActivatedButton)
        )
      },
      mustDisplayCustomerInformation() {
        let displayCustomerInformation = true
        if (
          !this.order.billing_address &&
          !this.order.shipping_address &&
          !this.order.customer.email &&
          !this.order.customer.last_name &&
          !this.order.customer.first_name &&
          !this.order.customer.phone &&
          !this.order.customer.last_four_digit_phone_number
        ) {
          displayCustomerInformation = false
        }
        return displayCustomerInformation
      },
      checkPickupCode: function () {
        const isStoreDelivery = this.order.carrier?.carrier_type === 'store_delivery'
        if (isStoreDelivery && this.order.has_pickup_code) {
          return this.orderSettings?.delivery?.pickup_code?.activate === true
        }
        return false
      },
      pickupDeadline() {
        return this.order?.pickup_deadline
      },
      maxExtensionDate() {
        return computeMaxExtensionDate(this.order, this.pickupLocationSettings, this.pickupLocation)
      },
      canDisplayModifyPickupDeadlineButton() {
        return canDisplayPickupDeadlineButton(this.pickupLocationSettings, this.isOctipasAdmin, this.roles, this.order)
      },
      maxExtensionDateReached() {
        return maxExtensionDateReached(this.maxExtensionDate, this.order.pickup_deadline.split('T')[0])
      },
      pickupCodeResendAllowed() {
        const codeSettings = this.orderSettings?.delivery?.pickup_code || {}
        const pickupCodeActivated = codeSettings.activate
        const allowedRoles = Array.isArray(codeSettings.resend_allowed_roles) ? codeSettings.resend_allowed_roles : []
        const isRoleAllowed = this.isOctipasAdmin || allowedRoles.some((role) => this.roles.includes(role))
        return pickupCodeActivated && isRoleAllowed && this.order?.has_pickup_code
      },
      canCreateReturn() {
        // Check if user has any role that's in the allowReturnCreationInShop array
        if (!this.allowReturnCreationInShop || this.allowReturnCreationInShop.length === 0) {
          return false
        }
        // Check if any user role matches allowed roles
        return this.roles.some((role) => this.allowReturnCreationInShop.includes(role))
      },
      canDisplayReturnCreationButton() {
        return (
          this.orderIsEligibleToReturn(this.order) &&
          this.partnerSettings.returns_access &&
          (this.isCustomerService || this.canCreateReturn)
        )
      },
      sortedDocuments() {
        if (this.documents.length) {
          let sortedDocuments = JSON.parse(JSON.stringify(this.documents))
          return sortedDocuments
            .sort((a, b) => {
              const order = {
                invoice: 1,
                credit_note: 2,
                warranty: 3,
                other: 4,
              }
              return order[a.type] - order[b.type]
            })
            .map((doc) => {
              const type = getOrderDocTypeTranslation(doc.type)
              return {
                ...doc,
                name: type ? `${type} - ${doc.name}` : doc.name,
              }
            })
        }
        return []
      },
      mustDisplayDocuments() {
        return this.documents.length > 0
      },
      allowedPaymentMethods() {
        const methods = this.orderSettings?.payments?.payment_methods || []
        return methods.filter(
          (pm) => pm.activate && (this.isOctipasAdmin || pm.allowed_roles?.some((role) => this.roles.includes(role)))
        )
      },
      canValidatePayments() {
        if (!this.order) return false
        return (
          this.order.current_status === 'waiting_payment' &&
          this.order.payment_validation_required === true &&
          isPaymentValidationStore(this.order, this.currentStore) &&
          this.allowedPaymentMethods.length > 0
        )
      },
      shouldDisplayActions() {
        return (
          this.shouldDisplayParcelDeliveryButton ||
          this.canDisplayModifyPickupDeadlineButton ||
          this.canDisplayCancelOrderButton ||
          this.canDisplayReturnCreationButton ||
          this.canValidatePayments
        )
      },
    },
    methods: {
      async get() {
        try {
          const path = '/v1/int/orders/' + this.$route.params.id
          const myInit = {
            headers: {
              Authorization: 'Bearer ' + this.$auth.accessToken,
            },
          }

          this.order = await this.$Amplify.API.get('orders', path, myInit)
          await this.loadDocuments(this.order.order_id)

          if (Object.keys(this.expand).length === 0) {
            for (let subOrder of this.order.sub_orders) {
              this.$set(this.expand, subOrder.order_id, false)
              this.customizationsPanel.push(subOrder.items.map(() => [0]))
            }
          }
        } catch (e) {
          console.log(e)
        }
      },
      async loadDocuments(orderId) {
        try {
          this.documents = await this.documentService.listByEntity('orders', orderId)
        } catch (e) {
          console.log(e)
          this.documents = []
        }
      },
      async patch(newStatus, cancelReason = undefined) {
        if (newStatus === 'cancelled') {
          this.cancellation = true
        } else {
          this.loadingBtn = true
        }

        let patchData = {
          current_status: newStatus,
          cancel_reason: cancelReason,
          customer: this.order.customer,
          items: this.order.items,
          pickup_code_to_check: this.order.pickup_code_to_check,
        }
        if (Array.isArray(this.order.custom) && this.order.custom.length) {
          patchData.custom = this.order.custom
        }
        if (Array.isArray(this.order.payments) && this.order.payments.length) {
          patchData.payments = this.order.payments
        }
        const patchedOrder = await OrderService.patch(this.$auth.accessToken, this.$route.params.id, patchData)
        if (patchedOrder) {
          this.order = patchedOrder
        }
        this.loadingBtn = false
        this.cancellation = false
      },
      cancel(cancelReason) {
        this.patch('cancelled', cancelReason)
      },
      openCustomerDeliveryModalOrUpdate: function () {
        if (this.order.type === 'click_and_reserve' || this.checkPickupCode) {
          this.customerDeliveryDialog = true
        } else {
          this.patch('delivered')
        }
      },
      validateCustomerDelivery: function (pickupCode) {
        this.order.pickup_code_to_check = pickupCode
        this.patch('delivered')
        this.customerDeliveryDialog = false
      },
      getStatusTranslation: function (statusValue) {
        const status = this.status.find((status) => status.value === statusValue)
        if (status) {
          return status.text
        } else return statusValue
      },
      getTypeTranslation: function (typeValue) {
        const type = this.orderTypeTranslations.find((type) => type.value === typeValue)
        if (type) {
          return type.text
        } else return typeValue
      },
      toggleExpand: function (orderId) {
        this.expand[orderId] = !this.expand[orderId]
      },
      orderAlreadyFullyReturned(order) {
        for (let item of order.items) {
          let returnedQuantity = item.returned_quantity || 0
          let validatedQuantity = item.validated_quantity || 0
          if (returnedQuantity < validatedQuantity) {
            return false
          }
        }
        return true
      },
      createReturn(orderId) {
        this.$router.push({ name: 'returnCreate', params: { id: orderId } })
      },
      orderIsEligibleToReturn(order) {
        return (
          ['delivered', 'partially_delivered'].includes(order.current_status) &&
          !['click_and_reserve'].includes(order.type)
        )
      },
      mustDisplayAnyLocker(subOrder) {
        const preparationLockerId = subOrder.preparation_locker_id
        const selectedPreparationLocker = preparationLockerId && preparationLockerId !== 'no_available_locker'
        const receptionLockerSelected =
          subOrder.reception_locker_id && subOrder.reception_locker_id !== 'no_available_locker'
        const deliveredSubOrder = ['delivered', 'partially_delivered'].includes(subOrder.current_status)
        return (selectedPreparationLocker || receptionLockerSelected) && !deliveredSubOrder
      },
      mustDisplayPreparationLocker(subOrder) {
        const preparationLockerId = subOrder.preparation_locker_id
        const selectedPreparationLocker = preparationLockerId && preparationLockerId !== 'no_available_locker'
        const isNotInterStoreToPickup = subOrder.current_status === 'to_pickup' && subOrder.type !== 'inter_store'
        const isNotSentPreparation = ['confirmed', 'partially_confirmed', 'waiting_carrier'].includes(
          subOrder.current_status
        )
        return selectedPreparationLocker && (isNotSentPreparation || isNotInterStoreToPickup)
      },
      setCancelReasons(settings) {
        const cancelReasonsTranslations = [
          { text: this.$t('subOrder.clientChangeOfMind'), value: 'client_changed_his_mind' },
          { text: this.$t('subOrder.deterioratedProducts'), value: 'deteriorated_products' },
          { text: this.$t('subOrder.orderedMultipleTimes'), value: 'ordered_multiple_times' },
          { text: this.$t('subOrder.productsNotAvailable'), value: 'products_not_available' },
          { text: this.$t('subOrder.customerDidNotCollect'), value: 'customer_did_not_collect_order' },
        ]
        if (settings?.display) {
          if (settings.display.order_cancel_reasons) {
            this.cancelReasons = []
            for (const orderCancelReason of settings.display.order_cancel_reasons) {
              const translatedCancelReason = cancelReasonsTranslations.find((translatedCancelReason) => {
                return orderCancelReason === translatedCancelReason.value
              })
              if (translatedCancelReason) {
                this.cancelReasons.push(translatedCancelReason)
              } else {
                this.cancelReasons.push({ text: orderCancelReason, value: orderCancelReason })
              }
            }
          } else {
            this.cancelReasons = cancelReasonsTranslations
          }
          if (settings.display.display_custom_cancel_reason) {
            this.cancelReasons.push({ text: this.$t('subOrder.otherReason'), value: 'other' })
          }
        } else {
          this.cancelReasons = cancelReasonsTranslations
        }
      },
      async onDeadlineChange(newDeadline) {
        this.loadingBtn = true
        const patchedOrder = await updatePickupDeadline(this.$auth.accessToken, this.order.order_id, newDeadline)
        if (patchedOrder) {
          this.order = patchedOrder
        }
        this.loadingBtn = false
      },
      deadlinesAreValid(checkedDeadlineDate) {
        return checkDeadlineIsValid(checkedDeadlineDate, this.pickupLocation, this.order.pickup_deadline)
      },
      getPickupCodeErrorMessage(error) {
        const message = error.response?.data?.message

        const errorMap = {
          user_not_authorized: this.$t('order.pickupCodeUnauthorized'),
          pickup_code_disabled: this.$t('order.pickupCodeFeatureDisabled'),
          pickup_code_not_found: this.$t('order.pickupCodeNotYetGenerated'),
          wrong_order_type: this.$t('order.pickupCodeNotApplicable'),
          order_not_found: this.$t('order.notFound'),
          pickup_location_not_found: this.$t('order.pickupLocationNotFound'),
        }

        return errorMap[message] || this.$t('order.errorLoadingPickupCode')
      },
      async resendPickupCode() {
        this.loadingResendPickupCode = true
        try {
          await OrderService.resendPickupCode(this.$auth.accessToken, this.$route.params.id)
          this.resendProductsDialog = false
        } catch (error) {
          console.error('Error resending pickup code:', error)

          EventBus.$emit('displaySnackbar', {
            display: true,
            color: 'error',
            text: this.getPickupCodeErrorMessage(error),
          })
        } finally {
          this.loadingResendPickupCode = false
        }
      },
      async validatePayments(payments) {
        this.loadingPaymentValidation = true
        try {
          const patchedOrder = await OrderService.patch(this.$auth.accessToken, this.$route.params.id, {
            current_status: 'waiting',
            payments,
          })
          if (patchedOrder) {
            this.order = patchedOrder
            this.paymentValidationDialog = false
          }
        } finally {
          this.loadingPaymentValidation = false
        }
      },
      async getReturnSettings(countryCode) {
        try {
          const settings = await this.returnSettingsService.getReturnSettings(countryCode)
          this.allowReturnCreationInShop =
            settings && settings.allow_return_creation_in_shop
              ? settings.allow_return_creation_in_shop
              : ['oms-shop-manager']
        } catch (e) {
          console.error(e)
          this.allowReturnCreationInShop = ['oms-shop-manager']
        }
      },
    },
  }
</script>
<style scoped>
  .print-button {
    display: inline;
    white-space: unset;
    width: 100%;
    font-size: 0.6575rem !important;
  }
  .v-btn-footer {
    max-width: 97%;
  }
  button >>> .v-btn__content {
    width: 100%;
    white-space: normal;
  }
  .expansion-header {
    cursor: pointer;
    min-height: 48px;
  }
</style>
